/**
 * AK-509 — 4-статусная модель занятия: единый источник для карточки поля,
 * карты и попапа сева.
 *
 * UAT founder (staging v1.1406.0): попап «Посев · сезон 2026» показывал ПЯТЬ
 * pill'ов (План · Посеян · Вегетация · Убирают · Убран). Лестница PR #573
 * (ADR-055 §D1, поправка 28.07.2026) на момент написания была законной, но
 * 30.07.2026 founder утвердил другую модель — 4 пользовательских значения
 * (Запланировано · Растёт · Убрано · Списано) — кодифицированную в
 * `docs/superpowers/adr/2026-07-30-adr-059-event-status-model.md` (ADR-059,
 * §D12, аддендум к ADR-055 §D8) и `docs/superpowers/specs/2026-07-30-status-model-sowing.md`
 * §3/§4a, а визуально — в wireframe #601 v4
 * (`docs/superpowers/prototypes/field-card-sowings-v2/`). ADR-059 ЗАМЕНЯЕТ
 * ADR-055 §D1 в части словаря статусов; сама поправка §D1 («статус ОДИН,
 * рука=автомат») в силе и никуда не делась.
 *
 * Что изменилось относительно лестницы #573:
 *   · «Посеян» — не шаг. Сев остаётся СОБЫТИЕМ (кнопка «Отметить: посеяно» с
 *     датой — механику кнопки не трогаем, см. FieldSowBlock/SowForm), но
 *     сразу после события занятие показывает «Растёт» — отдельного экрана
 *     «Посеян» в пользовательском словаре нет (ADR-059 §2, диаграмма
 *     состояний: Запланировано → Растёт по факту сева).
 *   · «Убирают» (`partial_harvest`) — не шаг. Частичная уборка — цифра
 *     «убрано X из Y га» РЯДОМ со статусом «Растёт» (посев ещё стоит на
 *     части площади), не отдельный pill (ADR-059 §4a.5 «частичное — всегда
 *     цифрой»; § 3 таблицы статусной модели посева).
 *   · «Списано» (`written_off`) — как и раньше, вне лестницы: терминальный
 *     исход, из которого выход только через меню статуса с причиной.
 *
 * AK-434 (контекст, не переоткрывается): статус занятия ОДИН — его сводит
 * сервер `crop_status_resolved()` (миграция 118): рука перебивает автомат,
 * автомат работает там, где руками не трогали. Клиенту приезжает уже
 * разрешённый код в `sowing.crop_status`. Здесь остаются ровно три вещи:
 *   · ПОДПИСЬ кода на языке агронома (одна на полосу, бейдж и попап карты);
 *   · раскладка кода на ШАГИ трёхшаговой полосы прогресса;
 *   · цифра площади рядом со статусом для частичных стадий.
 */

/**
 * Шаги полосы прогресса. Зеркало SOWING_STAGE_LADDER в field_card.py
 * (backend держит СВОЮ, более мелкую лестницу для гейта PATCH-переходов —
 * это НЕ то же самое, что пользовательская лестница здесь; см. ADR-059).
 * Полоса ГРУБЕЕ статуса: «Растёт», «Посеян частично» и «Убирают» — один и тот
 * же шаг, разница видна в подписи текущего шага и в цифре площади рядом.
 */
export const SOWING_STAGE_LADDER = ['planned', 'growing', 'harvested'] as const;

export type SowingStageCode = (typeof SOWING_STAGE_LADDER)[number];

/**
 * ЕДИНЫЙ словарь подписей — на полосу прогресса, бейдж статуса
 * (`components/fields/sowingStatus.ts` берёт подписи отсюда, оставляя себе
 * только тона) и попап карты. Пользователь видит РОВНО четыре значения
 * (ADR-059 §3): Запланировано · Растёт · Убрано · Списано — плюс «Пар»
 * отдельной осью (ADR-046, к общему циклу не относится).
 *
 * Пространство ВХОДНЫХ кодов шире четырёх слов: сервер по-прежнему может
 * прислать `sown` / `partial_sown` / `partial_harvest` (уточнения полноты
 * площади, ADR-055 D2, живут в схеме) — они сворачиваются в «Растёт», цифра
 * даётся отдельно через `sowingStageQuantity()`. Совпадающие коды — одно
 * понятие и одна подпись, чтобы бейдж под ладдером не спорил сам с собой.
 */
export const SOWING_STAGE_LABELS: Record<string, string> = {
  // --- 4 пользовательских значения (ADR-059 §3) ---
  planned: 'Запланировано',
  growing: 'Растёт',
  harvested: 'Убрано',
  written_off: 'Списано',
  // --- сев — событие, не отдельная стадия: код всё ещё может доехать из
  // ручной отметки или от старого backend'а, слово то же, что «Растёт» ---
  sown: 'Растёт',
  // --- уточнения полноты площади (вычисляемые, ADR-055 D2) — тот же
  // пользовательский статус, цифра «X из Y га» — отдельно, см. ниже ---
  partial_sown: 'Растёт',
  partial_harvest: 'Растёт',
  // --- запашка сидерата — плановое завершение цикла, тоже «Убрано» ---
  plowed_under: 'Убрано',
  // --- вне лестницы ---
  unplanned: 'Не запланировано',
  // Состояние поля, а не шаг лестницы: пар не сеют и не убирают (правка
  // founder 21.08 — «пар незасеян» на дашборде читалось как дыра в плане).
  fallow: 'Парует',
  // пересев — растущая культура, не финал (ADR-059 §2: подсев/пересев не
  // закрывают занятие смертью; смерть — только акт списания)
  reseeded: 'Растёт',
  failed: 'Погиб',
  // AK-513 (ревью архитектора #614): подписи `written_off` = «Списано» и
  // `plowed_under` = «Убрано» уже стоят выше — канон ADR-059 §D7 (пометка
  // «запахан (сидерат)» идёт цифрой/уточнением рядом, а не подменяет слово).
  // Дублирующие ключи из ветки сняты при merge origin/main.
  closed: 'Закрыт',
  finished: 'Пар завершён',
};

/**
 * Исходы, снимающие занятие с обычного хода сезона. Полосой не описываются:
 * «погиб» — не шаг между «посеян» и «убран». Двигать этап из них одним тапом
 * нельзя (иначе полоса воскрешает погибший посев) — только через меню статуса
 * посева, где записывается причина. Зеркало SOWING_STAGE_OUTCOMES в
 * field_card.py.
 *
 * `reseeded` сюда НЕ входит: пересев — это растущая культура, а не финал.
 */
export const SOWING_STAGE_OUTCOMES = [
  'failed',
  'written_off',
  'plowed_under',
  'closed',
  'finished',
] as const;

/**
 * Откат этапа назад одним тапом. Решение founder 28.07.2026 — разрешён.
 * Зеркало `ALLOW_STAGE_ROLLBACK` в field_card.py: держим оба конца в одном
 * состоянии, иначе UI предлагал бы шаг, на который сервер отвечает 422.
 * ВАЖНО: к исходам (`SOWING_STAGE_OUTCOMES`) откат не относится — оттуда
 * выход только через меню статуса посева, с причиной.
 */
export const ALLOW_STAGE_ROLLBACK = true;

/**
 * Код → шаг полосы. Статусы полноты, сев-событие и пересев кладутся на
 * шаг «Растёт» (индекс 1) — ADR-059 не различает их пользователю, полоса
 * показывает ФАЗУ, точное слово (если нужно) даёт цифра рядом.
 */
const STAGE_TO_STEP: Record<string, number> = {
  planned: 0,
  sown: 1,
  partial_sown: 1,
  growing: 1,
  reseeded: 1,
  partial_harvest: 1,
  harvested: 2,
};

/**
 * Минимум, который нужен от занятия, чтобы посчитать статус. Совместимо с
 * `OccupancySowing` из карточки поля — специально не импортируем его тип,
 * чтобы модулем могли пользоваться и другие экраны (карта, списки).
 */
export interface SowingStageSource {
  occupancy_type?: string | null;
  /**
   * Занятие поля паром по ЛЮБОМУ источнику — состояние поля или «паровая»
   * культура каталога. Пар не проходит лестницу сева: у него показывается
   * состояние одним словом («Парует»), см. `sowingStageLabel`.
   */
  is_fallow?: boolean | null;
  /** Разрешённый сервером ЕДИНЫЙ статус (crop_status_resolved). Приоритетнее всего. */
  crop_status?: string | null;
  status?: string | null;
  /** Нормализованный ручной этап от backend (совместимость). */
  stage?: string | null;
  crop_id?: string | null;
  local_catalog_item_id?: string | null;
  crop_name?: string | null;
}

/**
 * Источник для цифры площади рядом со статусом. Поля приходят с backend'а из
 * `crop_status_resolved()` (см. `OccupancySowing` в `components/fields/card/types.ts`)
 * — фронт их только форматирует, ничего не считает сам.
 */
export interface SowingQuantitySource extends SowingStageSource {
  /** посеяно ÷ площадь leaf'а (0..1). */
  sown_frac?: number | null;
  /** убрано ÷ посеянное (0..1); укосы не считаются. */
  harvested_frac?: number | null;
  harvested_area_ha?: number | null;
  leaf_area_ha?: number | null;
}

/**
 * Статус занятия: то, что показываем пользователю.
 *
 * Порядок источников: разрешённый сервером `crop_status` → нормализованный
 * ручной `stage` → сырой `status` с той же нормализацией на клиенте (ответ
 * старого backend'а). Правило нормализации одно: культура выбрана ⇒ занятие
 * как минимум «План», «дыры в плане» тут быть не может.
 */
export function sowingStage(sowing?: SowingStageSource | null): string | null {
  if (!sowing) return null;
  const hasCrop = Boolean(
    sowing.crop_id ?? sowing.local_catalog_item_id ?? sowing.crop_name,
  );
  const isFallow = sowing.occupancy_type === 'fallow';
  const raw = sowing.crop_status ?? sowing.stage ?? sowing.status ?? null;
  if (!raw) return isFallow || !hasCrop ? 'unplanned' : 'planned';
  if (raw === 'unplanned' && hasCrop && !isFallow) return 'planned';
  return raw;
}

/**
 * Подпись статуса — ОДНА на карточку поля, попап карты и бейдж.
 *
 * Незнакомый код НЕ выдаём за «План»: именно такая подмена и родила исходный
 * баг (полоса утверждала «План» там, где посев уже погиб). Нет подписи —
 * честное «Без статуса».
 */
export const FALLOW_STAGE_LABEL = 'Парует';
/** АМП паром не называем — у агромелиоративного поля своя подпись. */
export const AMP_STAGE_LABEL = 'АМП';

/** Занятие — пар по любому источнику: состояние поля или «паровая» культура. */
export function isFallowOccupancy(sowing?: SowingStageSource | null): boolean {
  if (!sowing) return false;
  return sowing.occupancy_type === 'fallow' || Boolean(sowing.is_fallow);
}

export function sowingStageLabel(sowing?: SowingStageSource | null): string {
  // Пар идёт мимо лестницы: у него показывается СОСТОЯНИЕ, а не этап сева.
  // Раньше занятие «Пар черный» (заведённое культурой) подписывалось
  // «Растёт»/«Запланировано» — для агронома это бессмыслица.
  if (isFallowOccupancy(sowing)) {
    const name = (sowing?.crop_name ?? '').trim().toUpperCase();
    return name.startsWith('АМП') ? AMP_STAGE_LABEL : FALLOW_STAGE_LABEL;
  }
  const stage = sowingStage(sowing);
  if (!stage) return 'Без статуса';
  return SOWING_STAGE_LABELS[stage] ?? 'Без статуса';
}

/**
 * Цифра рядом со статусом «Растёт» — «частичное всегда цифрой» (ADR-059
 * §4a.5). null — цифра не нужна (план, полная стадия, пар, исход). Лестница
 * больше не рисует «Убирают»/«Посеян частично» отдельным pill'ом — вместо
 * этого рядом со статусом встаёт строка вида «убрано 12,4 из 47,3 га».
 *
 * Порядок источников зеркалит `sowingStage()`: если площадей в ответе нет
 * (старый backend, либо `crop_status_resolved` не досчитал факты) — падаем
 * на процент (`*_frac`), а если и его нет — молчим, вместо неверной цифры.
 *
 * AK-736 (UAT founder 21.08, карточка БМЗ-079.1): гейт «только при
 * stage==='partial_harvest'» терял цифру, когда РУКА подтверждала «Растёт»
 * поверх частичной уборки (ADR-059 §D1 «рука бьёт автомат» —
 * `crop_status_resolved()` тогда отдаёт ручной 'growing', не дериватный
 * 'partial_harvest'). Факты при этом в ответе ЕСТЬ — `crop_status_facts()`
 * считает их независимо от источника показанного статуса. Карточка молчала
 * про 59,1 га, а вкладка операций честно писала «Уборка» — два экрана
 * спорили сами с собой. Гейт теперь по ТЕРМИНАЛУ (harvested/plowed_under —
 * там площадь убранного уже равна площади leaf'а, повторять её шумно), а не
 * по конкретному деривату.
 */
export function sowingStageQuantity(
  sowing?: SowingQuantitySource | null,
): string | null {
  if (!sowing) return null;
  const stage = sowingStage(sowing);
  const total = sowing.leaf_area_ha;
  const fmt = (v: number) =>
    v.toLocaleString('ru-RU', { maximumFractionDigits: 1 });

  const isHarvestedTerminal = stage === 'harvested' || stage === 'plowed_under';
  if (!isHarvestedTerminal) {
    if (sowing.harvested_area_ha != null && sowing.harvested_area_ha > 0 && total != null) {
      return `убрано ${fmt(sowing.harvested_area_ha)} из ${fmt(total)} га`;
    }
    if (
      stage === 'partial_harvest' &&
      (sowing.harvested_area_ha == null || sowing.harvested_area_ha <= 0) &&
      sowing.harvested_frac != null
    ) {
      return `убрано ${Math.round(sowing.harvested_frac * 100)} %`;
    }
  }

  if (stage === 'partial_sown') {
    if (sowing.sown_frac != null && total != null) {
      return `посеяно ${fmt(total * sowing.sown_frac)} из ${fmt(total)} га`;
    }
    if (sowing.sown_frac != null) {
      return `посеяно ${Math.round(sowing.sown_frac * 100)} %`;
    }
    return null;
  }

  return null;
}

/**
 * Индекс шага полосы. Вызывать ТОЛЬКО для статусов лестницы
 * (`isLadderStage`): для «погиб»/«закрыт» никакого шага не существует, и 0
 * здесь — не «План», а отсутствие ответа. Полоса такие статусы не рисует.
 */
export function sowingStageIndex(stage?: string | null): number {
  if (!stage) return 0;
  return STAGE_TO_STEP[stage] ?? 0;
}

/** Статус раскладывается на шаги полосы? Пар, «дыра в плане» и исходы — нет. */
export function isLadderStage(stage?: string | null): boolean {
  return !!stage && stage in STAGE_TO_STEP;
}

/** Статус — финал сезона (полоса read-only, вернуть можно только с причиной). */
export function isOutcomeStage(stage?: string | null): boolean {
  return !!stage && (SOWING_STAGE_OUTCOMES as readonly string[]).includes(stage);
}

/**
 * Посев ещё не в поле → можно отметить «посеяно» одной кнопкой.
 * Считается по РАЗРЕШЁННОМУ статусу, а не по сырому `sowings.status`: иначе
 * занятие с культурой и `status='unplanned'` навсегда остаётся без кнопки
 * (баг из UAT).
 */
export function canMarkSown(sowing?: SowingStageSource | null): boolean {
  if (!sowing || sowing.occupancy_type === 'fallow') return false;
  return sowingStage(sowing) === 'planned';
}

/** Следующий шаг вперёд. Последний / вне лестницы → null. */
export function nextSowingStage(stage?: string | null): SowingStageCode | null {
  if (!isLadderStage(stage)) return null;
  return SOWING_STAGE_LADDER[sowingStageIndex(stage) + 1] ?? null;
}
